/**
 * Pure helpers over `SeriesPoint[]` — bucketing, windowing, aggregation and
 * display formatting. Everything here is deterministic and UTC-based so a
 * metric tile and the chart next to it can never disagree about a day.
 */

import { formatCurrency } from '@/lib/formatters';
import type { SeriesPoint, TimeGrain, Unit } from './types';

export type Aggregate = 'sum' | 'last' | 'avg' | 'max';

export function monthKey(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Monday-anchored ISO week start for a `YYYY-MM-DD` day. */
export function weekKey(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export function bucketKey(iso: string, grain: TimeGrain): string {
  if (grain === 'month') return monthKey(iso);
  if (grain === 'week') return weekKey(iso);
  return iso.slice(0, 10);
}

export function addMonths(monthIso: string, n: number): string {
  const d = new Date(`${monthIso.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

export function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function stepBucket(key: string, grain: TimeGrain, n = 1): string {
  if (grain === 'month') return addMonths(key, n);
  return addDays(key, grain === 'week' ? 7 * n : n);
}

/** Every bucket key from `start` through `end` (inclusive) at `grain`. */
export function eachBucket(start: string, end: string, grain: TimeGrain): string[] {
  const out: string[] = [];
  let cursor = bucketKey(start, grain);
  const last = bucketKey(end, grain);
  let guard = 0;
  while (cursor <= last && guard < 5000) {
    out.push(cursor);
    cursor = stepBucket(cursor, grain);
    guard += 1;
  }
  return out;
}

export function aggregate(values: number[], agg: Aggregate): number {
  if (values.length === 0) return 0;
  switch (agg) {
    case 'sum':
      return values.reduce((s, v) => s + v, 0);
    case 'avg':
      return values.reduce((s, v) => s + v, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'last':
      return values[values.length - 1];
  }
}

/** Points whose `t` falls inside the inclusive window. */
export function sliceWindow(points: SeriesPoint[], start: string, end: string): SeriesPoint[] {
  return points.filter((p) => p.t >= start && p.t <= end);
}

/**
 * Month-grain series are matched on their month key so a window such as
 * "last 30 days" still picks up the month(s) it overlaps.
 */
export function sliceWindowMonths(points: SeriesPoint[], start: string, end: string): SeriesPoint[] {
  const s = monthKey(start);
  const e = monthKey(end);
  return points.filter((p) => p.t >= s && p.t <= e);
}

/** Roll a series into coarser buckets. Day → week/month, or month stays month. */
export function rollup(points: SeriesPoint[], grain: TimeGrain, agg: Aggregate): SeriesPoint[] {
  const buckets = new Map<string, number[]>();
  for (const p of points) {
    const k = bucketKey(p.t, grain);
    const arr = buckets.get(k);
    if (arr) arr.push(p.v);
    else buckets.set(k, [p.v]);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([t, vals]) => ({ t, v: aggregate(vals, agg) }));
}

/** Insert zero (or carried-forward) points so charts don't interpolate across gaps. */
export function fillGaps(
  points: SeriesPoint[],
  start: string,
  end: string,
  grain: TimeGrain,
  mode: 'zero' | 'carry' = 'zero',
): SeriesPoint[] {
  const byKey = new Map(points.map((p) => [p.t, p.v]));
  let last = 0;
  return eachBucket(start, end, grain).map((t) => {
    const v = byKey.get(t);
    if (v !== undefined) {
      last = v;
      return { t, v };
    }
    return { t, v: mode === 'carry' ? last : 0 };
  });
}

export function cumulative(points: SeriesPoint[]): SeriesPoint[] {
  let run = 0;
  return points.map((p) => {
    run += p.v;
    return { t: p.t, v: run };
  });
}

export function sumSeries(points: SeriesPoint[]): number {
  return points.reduce((s, p) => s + p.v, 0);
}

/** Percentage change as a 0..1 ratio; null when the baseline is 0 / missing. */
export function pctDelta(current: number, previous: number | null | undefined): number | null {
  if (previous == null || !Number.isFinite(previous) || previous === 0) return null;
  if (!Number.isFinite(current)) return null;
  return (current - previous) / Math.abs(previous);
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const ONE_DP = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const TWO_DP = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/**
 * Metric values travel in their *natural* unit: cents for currency,
 * 0..1 for percent / ratio, raw counts, seconds, days. Formatting is the
 * only place that knows how to turn each into text.
 */
export function formatUnit(value: number | null | undefined, unit: Unit, opts?: { compact?: boolean }): string {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (unit) {
    case 'currency':
      return formatCurrency(value, opts?.compact ? { compact: true } : { whole: Math.abs(value) >= 100_000 });
    case 'percent':
      return `${(value * 100).toFixed(Math.abs(value) < 0.1 ? 1 : 1)}%`;
    case 'ratio':
      return TWO_DP.format(value);
    case 'multiple':
      return `${TWO_DP.format(value)}×`;
    case 'days':
      return `${ONE_DP.format(value)} d`;
    case 'months':
      return `${ONE_DP.format(value)} mo`;
    case 'seconds':
      return formatSeconds(value);
    case 'score':
      return ONE_DP.format(value);
    case 'count':
    default:
      return opts?.compact && Math.abs(value) >= 10_000 ? COMPACT.format(value) : INT.format(value);
  }
}

/** Axis-friendly short formatter, e.g. `$12k`, `3.4k`, `45%`. */
export function formatAxis(value: number, unit: Unit): string {
  if (!Number.isFinite(value)) return '';
  switch (unit) {
    case 'currency':
      return formatCurrency(value, { compact: true, whole: true });
    case 'percent':
      return `${Math.round(value * 100)}%`;
    case 'multiple':
      return `${ONE_DP.format(value)}×`;
    case 'seconds':
      return formatSeconds(value);
    case 'days':
      return `${ONE_DP.format(value)}d`;
    case 'months':
      return `${ONE_DP.format(value)}mo`;
    default:
      return Math.abs(value) >= 1000 ? COMPACT.format(value) : ONE_DP.format(value);
  }
}

export function formatSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** Convert a value typed by the user in display units into the metric's natural unit. */
export function displayToNatural(value: number, unit: Unit): number {
  if (unit === 'currency') return Math.round(value * 100);
  if (unit === 'percent') return value / 100;
  return value;
}

export function naturalToDisplay(value: number, unit: Unit): number {
  if (unit === 'currency') return value / 100;
  if (unit === 'percent') return value * 100;
  return value;
}

export function unitInputSuffix(unit: Unit): string {
  switch (unit) {
    case 'currency':
      return '$';
    case 'percent':
      return '%';
    case 'days':
      return 'days';
    case 'months':
      return 'months';
    case 'seconds':
      return 'sec';
    case 'multiple':
      return '×';
    default:
      return '';
  }
}

const MONTH_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
const MONTH_LONG_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const DAY_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const DAY_LONG_FMT = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatBucket(t: string, grain: TimeGrain, long = false): string {
  const d = new Date(`${t}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return t;
  if (grain === 'month') return long ? MONTH_LONG_FMT.format(d) : MONTH_FMT.format(d);
  if (grain === 'week') return `Wk of ${DAY_FMT.format(d)}`;
  return long ? DAY_LONG_FMT.format(d) : DAY_FMT.format(d);
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function currentMonthKey(): string {
  return monthKey(todayIso());
}
